import {
  type Client,
  createBatchEventProcessor,
  createInstance,
  createPollingProjectConfigManager,
} from '@optimizely/react-sdk';

/**
 * Optimizely is only active when it is explicitly switched on and an SDK key is
 * configured. Flipping VITE_OPTIMIZELY_ENABLED to 'false' is how you disable
 * experimentation locally without blanking out the key.
 */
export const isOptimizelyEnabled = (): boolean => (
  import.meta.env.VITE_OPTIMIZELY_ENABLED === 'true'
  && !!import.meta.env.VITE_OPTIMIZELY_SDK_KEY
);

/**
 * Builds a client, or returns null when Optimizely is inactive. Returning null
 * rather than a stub means no datafile polling and no event batching happen when
 * the integration is switched off.
 *
 * Exported separately from the singleton below so tests can exercise env
 * permutations without reloading the module.
 */
export const buildOptimizelyClient = (): Client | null => {
  if (!isOptimizelyEnabled()) {
    return null;
  }

  return createInstance({
    eventProcessor: createBatchEventProcessor(),
    projectConfigManager: createPollingProjectConfigManager({
      sdkKey: import.meta.env.VITE_OPTIMIZELY_SDK_KEY,
    }),
  });
};

export const optimizelyClient = ((): Client | null => {
  try {
    return buildOptimizelyClient();
  } catch {
    return null;
  }
})();
